import { Button } from 'primereact/button';
import classNames from 'classnames';
import { Tooltip } from 'primereact/tooltip';
import dayjs from 'dayjs';
import { InputSwitch } from 'primereact/inputswitch';

import { ColumnDescription } from '@/components/common/ui/table/ColumnDescription';
import {
  SUBSCRIPTION_PAYMENT_STATUS,
  STAGE_GRADUATION_STATUS,
  INACTIVE_REASON,
} from '@/shared/constants/user';

export const UserColumns = (
  t: any,
  onEditUser?: (id: number) => void,
  onEditStatusUser?: (isActive: boolean, rowData: any) => void,
): ColumnDescription[] => [
  {
    dataField: 'full_name_kanji',
    text: t('user_list.full_name_coach'),
    classname: 'min-w-[104px]',
    formatter: (rowData: any) => {
      return (
        <div>
          <Tooltip target={`.tooltip-name-${rowData.id}`}>
            {rowData.full_name_kanji}
          </Tooltip>
          <div
            className={classNames(
              'line-clamp-1 text-gray-900 break-all',
              `tooltip-name-${rowData.id}`,
            )}
            data-pr-position="top"
          >
            {rowData.full_name_kanji}
          </div>
        </div>
      );
    },
  },
  {
    dataField: 'email',
    text: t('user_list.email'),
    classname: 'min-w-[154px]',
    formatter: (rowData: any) => {
      return (
        <div>
          <Tooltip target={`.tooltip-email-${rowData.id}`}>
            {rowData.email}
          </Tooltip>
          <div
            className={classNames(
              'line-clamp-1 text-gray-900 break-all',
              `tooltip-email-${rowData.id}`,
            )}
            data-pr-position="top"
          >
            {rowData.email}
          </div>
        </div>
      );
    },
  },
  {
    dataField: 'graduation_terms',
    text: t('user_list.graduation_terms'),
    sort: true,
    headerClassname: 'min-w-[110px] text-center',
    classname: 'min-w-[110px] text-center',
  },
  {
    dataField: 'plan',
    text: t('user_list.plan'),
    headerClassname: 'min-w-[110px]',
    classname: 'min-w-[110px]',
    formatter: (rowData: any) => {
      return (
        <div>
          <Tooltip target={`.tooltip-plan-${rowData.id}`}>
            {rowData.plan?.name}
          </Tooltip>
          <div
            className={classNames(
              'line-clamp-1 text-gray-900 break-all',
              `tooltip-plan-${rowData.id}`,
            )}
            data-pr-position="top"
          >
            {rowData.plan?.name || '-'}
          </div>
        </div>
      );
    },
  },
  {
    dataField: 'graduation_term',
    text: t('user_list.course'),
    headerClassname: 'min-w-[110px]',
    classname: 'min-w-[110px]',
    formatter: (rowData: any) => {
      return (
        <div>{rowData.graduation_term?.name ?? '-'}</div>
      );
    },
  },
  {
    dataField: 'created_at',
    text: t('user_list.updated_at'),
    classname: 'w-[200px]',
    sort: true,
    formatter: (rowData) => (
      <div>
        {rowData.created_at
          ? dayjs(rowData.created_at.toString()).format('YYYY/MM/DD')
          : ''}
      </div>
    ),
  },
  {
    dataField: 'stage_graduation_status',
    text: t('user_list.progress'),
    headerClassname: 'w-[150px] text-center',
    classname: 'w-[150px] text-center',
    formatter: (rowData) => {
      return (
        <div className="w-full flex justify-center">
          {rowData.stage_graduation_status === STAGE_GRADUATION_STATUS.PAID && (
            <div className="rounded-2xl bg-success-50 text-success-700 text-md font-medium leading-4 w-[66px] h-[24px] flex justify-center items-center">
              {t('user_list.graduation_billing')}
            </div>
          )}

          {rowData.stage_graduation_status === STAGE_GRADUATION_STATUS.FREE && (
            <div className="rounded-2xl bg-warning-50 text-warning-500 text-md font-medium leading-4 w-[93px] h-[24px] flex justify-center items-center">
              {t('user_list.graduation_free_for_coach')}
            </div>
          )}

          {rowData.stage_graduation_status ===
            STAGE_GRADUATION_STATUS.TRIAL && (
            <div className="rounded-2xl bg-primary-50 text-primary-700 text-md font-medium leading-4 w-[93px] h-[24px] flex justify-center items-center">
              {t('user_list.graduation_free_for_student')}
            </div>
          )}
        </div>
      );
    },
  },
  {
    dataField: 'subscription_payment_status',
    text: t('user_list.payment_status'),
    headerClassname: 'w-[200px] text-center',
    classname: 'w-[200px]',
    formatter: (rowData) => {
      return (
        <div className="w-full flex justify-center">
          {rowData.subscription_payment_status ===
            SUBSCRIPTION_PAYMENT_STATUS.CURRENT && (
            <div className="rounded-2xl bg-success-50 text-success-700 text-md font-medium leading-4 w-[66px] h-[24px] flex justify-center items-center">
              {t('user_list.subscription_payment_current')}
            </div>
          )}

          {rowData.subscription_payment_status ===
            SUBSCRIPTION_PAYMENT_STATUS.SUSPENDED && (
            <div className="rounded-2xl bg-error-50 text-error-600 text-md font-medium leading-4 w-[80px] h-[24px] flex justify-center items-center">
              {t('user_list.subscription_payment_suspended')}
            </div>
          )}

          {rowData.subscription_payment_status ===
            SUBSCRIPTION_PAYMENT_STATUS.CANCELED && (
            <div className="rounded-2xl bg-gray-50 text-gray-600 text-md font-medium leading-4 w-[80px] h-[24px] flex justify-center items-center">
              {t('user_list.subscription_payment_canceled')}
            </div>
          )}

          {!rowData.subscription_payment_status && '-'}
        </div>
      );
    },
  },
  {
    dataField: 'reason',
    text: t('user_list.payment_detail'),
    headerClassname: 'w-[145px]',
    classname: 'w-[145px]',
    formatter: (rowData) => (
      <div>
        {rowData.inactive_reason === INACTIVE_REASON.UNIVAPAY_CANCEL_PAYMENT &&
          'Univapayより削除'}

        {rowData.inactive_reason === INACTIVE_REASON.DELETE_ACCOUNT &&
          '退会済み'}

        {rowData.inactive_reason === INACTIVE_REASON.PAYMENT_FAILED &&
          '課金上限失敗'}

        {!rowData.inactive_reason && '-'}
      </div>
    ),
  },
  {
    dataField: 'action',
    text: '',
    sort: false,
    classname: 'text-center [&>div]:justify-center w-[160px]',
    headerClassname: '[&>div]:justify-center',
    formatter: (rowData) => (
      <div className="flex flex-row h-[36px] items-center">
        <InputSwitch
          checked={rowData.status === 'active' ? true : false}
          onChange={(e) => onEditStatusUser?.(e.value, rowData)}
        />
        <Button
          className="mx-3 p-[10px]"
          onClick={() => onEditUser?.(rowData.id)}
        >
          <span className="pi pi-pencil" />
        </Button>
      </div>
    ),
  },
];
